import { useState, useEffect, useCallback, useRef } from "react";
import { getDashboard } from "@/Api/dashboard";
import { DashboardData, DashboardFilters as FiltersType } from "@/types/dashboard";
import { DashboardFilters } from "./components/DashboardFilters";
import { TodayCard } from "./components/TodayCard";
import { TodayCardLarge } from "./components/TodayCardLarge";
import { MonthCard } from "./components/MonthCard";
import { RhythmCard } from "./components/RhythmCard";
import { SalesByDayChart } from "./components/SalesByDayChart";
import { AverageTicketCard } from "./components/AverageTicketCard";
import { ClientsAttendedCard } from "./components/ClientsAttendedCard";
import { SellersRankingCard } from "./components/SellersRankingCard";
import { TopProductsCard } from "./components/TopProductsCard";
import { AlertsCard } from "./components/AlertsCard";
import { SellerCommissionCardSmall } from "./components/SellerCommissionCardSmall";
import { Loader2, RefreshCw, LayoutDashboard, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/hooks/auth";
import { TypeUser } from "@/types/userType";

const AUTO_REFRESH_INTERVAL = 20; // seconds

export function Dashboard() {
  const user = useAuth()?.user;
  const isSeller = user?.user_type_id === TypeUser.seller;
  const currentSellerId = isSeller ? user?.seller?.id : undefined;
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState<FiltersType>({
    goal_type: "meta",
    chart_period: "month",
    period: "today",
  });
  const isFirstLoad = useRef(true);

  // Auto-refresh state
  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL);
  const [isPaused, setIsPaused] = useState(false);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const loadDashboard = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const response = await getDashboard(filters);
      if (response.data?.response) {
        setData(response.data.response);
      }
    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDashboard(isFirstLoad.current);
    isFirstLoad.current = false;
  }, [loadDashboard]);

  // Auto-refresh countdown effect
  useEffect(() => {
    if (isPaused || loading) {
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      return;
    }

    // Countdown timer
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return AUTO_REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    // Auto-refresh interval
    refreshIntervalRef.current = setInterval(() => {
      loadDashboard(false);
    }, AUTO_REFRESH_INTERVAL * 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [isPaused, loading, loadDashboard]);

  const handleFiltersChange = (newFilters: FiltersType) => {
    setFilters(newFilters);
  };

  const handleChartPeriodChange = (period: "today" | "yesterday" | "week" | "month") => {
    setFilters((prev) => ({ ...prev, chart_period: period }));
  };

  const handleRefresh = () => {
    loadDashboard(false);
    setCountdown(AUTO_REFRESH_INTERVAL);
  };

  const togglePause = () => {
    setIsPaused((prev) => !prev);
    if (isPaused) {
      setCountdown(AUTO_REFRESH_INTERVAL);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <div className="p-4 bg-primary/10 rounded-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <p className="text-sm text-muted-foreground">Carregando dashboard...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <p className="text-muted-foreground">Erro ao carregar dados do dashboard</p>
        <Button onClick={() => loadDashboard()}>Tentar novamente</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={LayoutDashboard}
        title="Dashboard"
        description="Acompanhe suas vendas e metas em tempo real"
      >
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm">
            <button
              onClick={togglePause}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
              title={isPaused ? "Retomar atualização automática" : "Pausar atualização automática"}
            >
              {isPaused ? (
                <Play className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              ) : (
                <Pause className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              )}
            </button>
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-1.5 text-sm">
              <RefreshCw
                className={`h-3.5 w-3.5 text-slate-500 ${!isPaused && !loading ? "animate-spin" : ""}`}
                style={{ animationDuration: "3s" }}
              />
              <span className="font-mono text-slate-600 dark:text-slate-400 w-6 text-center">
                {isPaused ? "--" : `${countdown}s`}
              </span>
            </div>
          </div>
        </div>
      </PageHeader>

      <DashboardFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {/* Card de Vendas do Período - só para vendedores (grande em cima) */}
      {isSeller && (
        <div className="mb-4">
          <TodayCardLarge data={data.today} period={filters.period} goalType={filters.goal_type} requiredDailyAverage={data.rhythm.required_daily_average} />
        </div>
      )}

      {/* Cards principais - Layout horizontal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {isSeller ? (
          <>
            <SellerCommissionCardSmall period={filters.period} startDate={filters.start_date} endDate={filters.end_date} />
            <MonthCard data={data.month} goalType={filters.goal_type} />
            <RhythmCard data={data.rhythm} goalType={filters.goal_type} />
          </>
        ) : (
          <>
            <TodayCard data={data.today} period={filters.period} goalType={filters.goal_type} requiredDailyAverage={data.rhythm.required_daily_average} />
            <MonthCard data={data.month} goalType={filters.goal_type} />
            <RhythmCard data={data.rhythm} goalType={filters.goal_type} />
          </>
        )}
      </div>

      {/* Grafico e Ranking */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Grafico ocupa 2 colunas */}
        <div className="lg:col-span-2">
          <SalesByDayChart
            data={data.sales_by_day}
            period={filters.chart_period || "month"}
            onPeriodChange={handleChartPeriodChange}
          />
        </div>
        {/* Ranking em 1 coluna */}
        <div className="lg:col-span-1">
          <SellersRankingCard data={data.ranking_position} goalType={filters.goal_type} currentSellerId={currentSellerId} />
        </div>
      </div>

      {/* Cards informativos */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Coluna 1: Ticket Médio e Clientes Atendidos empilhados */}
        <div className="flex flex-col gap-4">
          <AverageTicketCard data={data.average_ticket} period={filters.period} />
          <ClientsAttendedCard data={data.clients_attended} period={filters.period} />
        </div>
        {/* Coluna 2 e 3: Top Produtos */}
        <div className="lg:col-span-2">
          <TopProductsCard data={data.top_products} period={filters.period} />
        </div>
        {/* Coluna 4: Alertas */}
        <div>
          <AlertsCard data={data.alerts} />
        </div>
      </div>
    </div>
  );
}
